/**
 * Hitelesített `fetch` — a nyers `fetch`-út (3. út, `utils/session.ts` fejléce) KÖZÖS burkolója.
 * [Web 7 / T1, 2026-10-05]
 *
 * MIÉRT: a csendes bejelentkezés-megújítás (`utils/tokenRefresh.ts`) az axios-utakon interceptorral
 * működik, a nyers `fetch`-et viszont semmilyen interceptor nem látja. KORÁBBAN (2. csomag) minden
 * hívó-hely maga olvasta ki a tokent (`localStorage.getItem('token')`), maga tette a `Bearer` fejlécbe,
 * és maga hívta a `reportFetchUnauthorized(response, token)`-t. Ma mindez EGY helyen van:
 *   1. a kérés INDULÁSAKOR a token ellenőrzése, szükség esetén csendes megújítás (`resolveRequestToken`);
 *   2. a `Authorization: Bearer <token>` fejléc (a hívó NEM ad meg sajátot — ha mégis, ez felülírja);
 *   3. 401-re EGY megújítási kísérlet és a kérés megismétlése (`afterUnauthorized`), ha a kérés
 *      indulásakor még nem volt kísérlet; CSAK 401-re (a 403 / 5xx változatlanul a hívóhoz megy);
 *   3/b. [Web 7 / T1 javító kör 2, 2026-10-06] F-A: ha a kérés megújítása ÁTMENETI hibával bukott, a 401
 *      helyett egy SZINTETIKUS 503-as JSON-válasz (`{ message: REFRESH_UNAVAILABLE_MESSAGE }`) jön
 *      vissza — nincs lejárat-jelzés, a tároló marad. A hívók `response.ok` / `response.text()` +
 *      `responseErrorMessage` / `saveResponseErrorMessage` útja ezt változtatás nélkül kiírja;
 *   4. a végső 401 a közös jelzőn (`reportFetchUnauthorized`) a TÉNYLEGESEN ELKÜLDÖTT tokennel — így a
 *      lejárt-zár MÉG a hívó `response.ok` vizsgálata ELŐTT áll (a mentési hibaszöveg, `saveResponseErrorMessage`,
 *      erre épül).
 * A forrás-őr (`utils/__tests__/sessionFetchGuard.test.ts`) azt igazolja, hogy a nyilvános két fájlon és
 * ezen kívül sehol nincs nyers `fetch(`.
 *
 * A kérés-törzs ismétléskor újra elküldhető: a hívók `JSON.stringify` szöveget vagy `FormData`-t adnak.
 */
import { reportFetchUnauthorized } from './session';
import { afterUnauthorized, REFRESH_UNAVAILABLE_MESSAGE, RefreshAttempt, resolveRequestToken } from './tokenRefresh';

function readStoredToken(): string | null {
  try {
    return localStorage.getItem('token');
  } catch {
    return null;
  }
}

/** A hívó fejléceit sima objektummá alakítja (a tesztek és a hívók `headers.Authorization`-t olvasnak). */
function toPlainHeaders(headers: HeadersInit | undefined): Record<string, string> {
  if (!headers) return {};
  if (typeof Headers !== 'undefined' && headers instanceof Headers) {
    const out: Record<string, string> = {};
    headers.forEach((v, k) => {
      out[k] = v;
    });
    return out;
  }
  if (Array.isArray(headers)) return Object.fromEntries(headers);
  return { ...(headers as Record<string, string>) };
}

function withAuthorization(init: RequestInit | undefined, token: string | null): RequestInit {
  const headers = toPlainHeaders(init?.headers);
  Object.keys(headers).forEach((k) => {
    if (k.toLowerCase() === 'authorization') delete headers[k];
  });
  if (token) headers.Authorization = `Bearer ${token}`;
  return { ...init, headers };
}

/**
 * [Web 7 / T1 javító kör 2, 2026-10-06] F-A: a 401 helyett visszaadott válasz, ha a megújítás ÁTMENETI
 * hibával bukott. 503 + JSON `{ message }` — a hívók meglévő hibakezelése (`responseErrorMessage`) a magyar
 * mondatot írja ki; a 401-specifikus lejárat-szövegek (`saveResponseErrorMessage` 401-ága) nem futnak rá.
 */
function refreshUnavailableResponse(): Response {
  return new Response(JSON.stringify({ message: REFRESH_UNAVAILABLE_MESSAGE }), {
    status: 503,
    statusText: 'Service Unavailable',
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function authFetch(url: string, init?: RequestInit): Promise<Response> {
  let token = readStoredToken();
  // [Web 7 / T1 javító kör 2, 2026-10-06] F-B: az indításkori kísérlet sorsa a 401-ágnak KÖTELEZŐEN
  // továbbadva — így kérésenként legfeljebb EGY megújítás fut (KORÁBBAN egy `refreshTried` jelző).
  let attempt: RefreshAttempt = 'none';
  if (token) {
    const resolved = await resolveRequestToken(token, url);
    token = resolved.token;
    attempt = resolved.attempt;
  }

  let response = await fetch(url, withAuthorization(init, token));

  // F-C: CSAK 401-re (a 403 jogosultság-, az 5xx szerverhiba — megújítás nem segít rajtuk).
  if (response.status === 401 && token) {
    const next = await afterUnauthorized(token, url, attempt);
    if (next.kind === 'retry') {
      token = next.token;
      response = await fetch(url, withAuthorization(init, token));
    } else if (next.kind === 'unavailable') {
      return refreshUnavailableResponse();
    }
  }

  reportFetchUnauthorized(response, token, url);
  return response;
}
